import React, { useEffect, useState } from 'react';
import {
  TextField,
  Typography,
  CircularProgress
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import BaseModal from '../../../utils/BaseModal';
import api from '../../../../api/axios';

type DeletionRequirements = {
  hasOpenTrades: boolean;
  hasOpenTransactions: boolean;
  connectedSteamAccounts: number;
  balance: number;
};

type Props = {
  open: boolean;
  userId: number | bigint | null | undefined;
  onClose: () => void;
  onDeleted: () => void;
};

const AccountDeleteModal: React.FC<Props> = ({ open, userId, onClose, onDeleted }) => {
  const { t } = useTranslation();
  const [confirmationText, setConfirmationText] = useState('');
  const [loading, setLoading] = useState(false);
  const [requirementsLoading, setRequirementsLoading] = useState(true);
  const [requirements, setRequirements] = useState<DeletionRequirements | null>(null);

  const deleteConfirmationWord = t('profile.security.delete.confirmWord');

  useEffect(() => {
    if (!open || !userId) return;

    const fetchRequirements = async () => {
      setRequirementsLoading(true);
      try {
        const response = await api.get<DeletionRequirements>(`/user/${userId}/deletion-requirements`);
        setRequirements(response.data);
      } catch (error) {
        // Silent error
      } finally {
        setRequirementsLoading(false);
      }
    };

    fetchRequirements();
  }, [open, userId]);

  const handleDelete = async () => {
    if (!userId) return;

    setLoading(true);
    try {
      await api.post(`/user/${userId}/delete-user`);
      onDeleted();
      onClose();
    } catch (error) {
      // Error handling via interceptor
    } finally {
      setLoading(false);
    }
  };

  const hasIssues = requirements && (
      requirements.hasOpenTrades ||
      requirements.hasOpenTransactions ||
      requirements.connectedSteamAccounts > 1
  );

  const isSubmitDisabled =
      loading ||
      requirementsLoading ||
      hasIssues ||
      confirmationText !== deleteConfirmationWord;

  // Helper to render requirement status
  const RequirementRow = ({ isError, text }: { isError: boolean; text: string }) => (
      <div className="flex items-center space-x-2">
        <div className={`w-1.5 h-1.5 rounded-full ${isError ? 'bg-red-500 shadow-[0_0_5px_rgba(239,68,68,0.5)]' : 'bg-emerald-500'}`} />
        <Typography className={`text-xs ${isError ? 'text-red-600 dark:text-red-400 font-medium' : 'text-gray-400 dark:text-neutral-500'}`}>
          {text}
        </Typography>
      </div>
  );

  return (
      <BaseModal
          open={open}
          title={t('profile.security.delete.modal.title')}
          onAbort={onClose}
          onSubmit={handleDelete}
          abortText={t('profile.security.common.cancel')}
          submitText={t('profile.security.delete.confirm')}
          submitColor="error"
          submitDisabled={isSubmitDisabled}
          maxWidth="xs"
      >
        {requirementsLoading ? (
            <div className="flex justify-center p-8"><CircularProgress size={24} /></div>
        ) : (
            <div className="space-y-6">
              <div className="space-y-4">
                {/* Balance Warning (only if balance > 0) */}
                {requirements?.balance && requirements.balance > 0 && (
                    <div className="p-3 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg">
                      <Typography className="text-[11px] text-orange-700 dark:text-orange-400 leading-relaxed">
                        {t('profile.security.delete.warning.balance', { amount: requirements.balance })}
                      </Typography>
                    </div>
                )}

                {/* Static Requirements Section - Always Visible */}
                <div className="p-4 bg-gray-50 dark:bg-neutral-800/50 border border-gray-100 dark:border-neutral-700/50 rounded-2xl space-y-3">
                  <Typography className="text-[10px] font-black uppercase tracking-widest text-gray-400 dark:text-neutral-500">
                    {t('profile.security.delete.requirements.title')}
                  </Typography>
                  <div className="space-y-2">
                    <RequirementRow
                        isError={!!requirements?.hasOpenTrades}
                        text={t('profile.security.delete.requirements.trades')}
                    />
                    <RequirementRow
                        isError={!!requirements?.hasOpenTransactions}
                        text={t('profile.security.delete.requirements.transactions')}
                    />
                  </div>
                </div>
              </div>

              {/* Input Section - Only if all requirements are met */}
              {!hasIssues ? (
                  <div className="space-y-5 pt-2 animate-in fade-in slide-in-from-bottom-2 duration-500">
                    <Typography className="text-gray-600 dark:text-neutral-400 text-sm leading-relaxed">
                      {t('profile.security.delete.modal.text')}
                    </Typography>

                    <div className="space-y-2">
                      <Typography className="text-[10px] font-bold text-gray-400 dark:text-neutral-500 tracking-wider uppercase">
                        {t('profile.security.delete.confirmInputLabel', { word: deleteConfirmationWord })}
                      </Typography>
                      <TextField
                          value={confirmationText}
                          onChange={(e) => setConfirmationText(e.target.value)}
                          fullWidth
                          size="small"
                          placeholder={deleteConfirmationWord}
                          autoComplete="off"
                          InputProps={{
                            className: "dark:bg-neutral-900/50 rounded-xl border-none"
                          }}
                          sx={{
                            "& .MuiOutlinedInput-notchedOutline": {
                              borderColor: "rgba(255,255,255,0.05)"
                            },
                            "&:hover .MuiOutlinedInput-notchedOutline": {
                              borderColor: "rgba(255,255,255,0.1)"
                            },
                            "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
                              borderColor: "rgba(239,68,68,0.5)"
                            }
                          }}
                      />
                    </div>
                  </div>
              ) : (
                  <div className="p-3 rounded-xl bg-red-500/5 border border-red-500/10 text-center">
                    <Typography className="text-[11px] font-medium text-red-500/80">
                      Du musst erst alle Bedingungen erfüllen, um fortzufahren.
                    </Typography>
                  </div>
              )}
            </div>
        )}
      </BaseModal>
  );
};

export default AccountDeleteModal;